// App router — three buckets:
//   /login            standalone (Login renders its own shell)
//   /admin/*          AdminShell (Compliance/Manager layout)
//   everything else   AppShell (DevOps daily-driver layout)
//
// v241 — first-run setup wizard (item 76). When /api/public-config
// reports setup_required=true, the SetupRedirect wrapper bounces
// authenticated traffic to /setup before any feature page renders.
// /login + /setup + /admin/system/setup are exempt from the redirect
// so the wizard itself + the manual re-run path stay reachable.

import { Loader2 } from 'lucide-react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { usePublicConfig } from './api/publicConfig';
import { useWhoami } from './api/client';
import type { ReactNode } from 'react';
import { Home } from './pages/Home';
import { Repos } from './pages/Repos';
import { RepoDetail } from './pages/RepoDetail';
import { ArtifactDetail } from './pages/ArtifactDetail';
import { Projects } from './pages/Projects';
import { Tokens } from './pages/Tokens';
import { Login } from './pages/Login';
import { AdminOverview } from './pages/admin/AdminOverview';
import { AdminMaintenance } from './pages/admin/AdminMaintenance';
// v185 IA cleanup (item 18): the three notifications-adjacent pages
// and the two imports pages are consolidated into hubs. Old paths
// (/admin/notification-channels, /admin/webhooks, /admin/imports/bulk)
// redirect into the hub with the right tab pre-selected; the
// individual page exports remain for any direct importers.
import { AdminNotificationsHub } from './pages/admin/AdminNotificationsHub';
import { AdminImportsHub } from './pages/admin/AdminImportsHub';
import { AdminAudit } from './pages/admin/AdminAudit';
import { AdminPermissions } from './pages/admin/AdminPermissions';
import { AdminQuotas } from './pages/admin/AdminQuotas';
import { AdminSCIM } from './pages/admin/AdminSCIM';
import { AdminLicense } from './pages/admin/AdminLicense';
import { AdminBranding } from './pages/admin/AdminBranding';
import { AdminBanners } from './pages/admin/AdminBanners';
import { AdminNetwork } from './pages/admin/AdminNetwork';
import { AdminTrust } from './pages/admin/AdminTrust';
import { AdminRetention } from './pages/admin/AdminRetention';
import { AdminCNPGCatalog } from './pages/admin/AdminCNPGCatalog';
import { AdminPullGate } from './pages/admin/AdminPullGate';
import { AdminGeo } from './pages/admin/AdminGeo';
import { AdminApiReference } from './pages/admin/AdminApiReference';
import { AdminSeed } from './pages/admin/AdminSeed';
import { AdminSlugAudit } from './pages/admin/AdminSlugAudit';
import { AdminCompliance } from './pages/admin/AdminCompliance';
import { AdminObservability } from './pages/admin/AdminObservability';
import { AdminSupport } from './pages/admin/AdminSupport';
import { ServiceAccountsPage } from './pages/ServiceAccounts';
import { ProjectMembers } from './pages/ProjectMembers';
import { LicenseCompliance } from './pages/projects/LicenseCompliance';
import { OllamaModels } from './pages/projects/OllamaModels';
import { KustomizeComponents } from './pages/projects/KustomizeComponents';
import { OPABundles } from './pages/projects/OPABundles';
import { AgentSkillPackages } from './pages/projects/AgentSkillPackages';
import { Quarantine } from './pages/projects/Quarantine';
import { Builds } from './pages/Builds';
import { ProjectBuilds } from './pages/projects/Builds';
import { BuildDetail } from './pages/projects/BuildDetail';
import { BuildDependencySearch } from './pages/projects/BuildDependencySearch';
import { Trash } from './pages/Trash';
import { Settings } from './pages/Settings';
import { Promotions } from './pages/Promotions';
import { Search } from './pages/Search';
import { SecurityList } from './pages/security/SecurityList';
import { SecurityNew } from './pages/security/SecurityNew';
import { SecurityAudit } from './pages/security/SecurityAudit';
import { WhoPulled } from './pages/security/WhoPulled';
import { Advisories } from './pages/security/Advisories';
import { AdvisoryDetail } from './pages/security/AdvisoryDetail';
import { DetectionFindings } from './pages/detection/Findings';
import { DetectionFindingDetail } from './pages/detection/FindingDetail';
import { DetectionRules } from './pages/detection/Rules';
import { DetectionScanners } from './pages/detection/Scanners';
import { DetectionQueue } from './pages/detection/Queue';
import { DetectionDashboard } from './pages/detection/Dashboard';
import { AppShell } from './components/AppShell';
import { SetupWizard } from './pages/SetupWizard';
import { AdminSystemSetup } from './pages/admin/AdminSystemSetup';

// SetupRedirect bounces authenticated traffic to /setup whenever the
// public-config endpoint says the wizard hasn't been completed. Paths
// that the wizard itself uses (/setup) or that an admin needs to
// recover (/login, /admin/system/setup) are passed through unchanged.
function SetupRedirect({ children }: Readonly<{ children: ReactNode }>) {
  const { data, isLoading } = usePublicConfig();
  const { pathname } = useLocation();
  const exempt =
    pathname === '/login' ||
    pathname === '/setup' ||
    pathname.startsWith('/admin/system/setup');
  if (isLoading || !data) return <>{children}</>;
  if (data.setup_required && !exempt) return <Navigate to="/setup" replace />;
  return <>{children}</>;
}

// RequireAuth gates the whole app behind a resolved session so an
// unauthenticated visitor never sees the app chrome (sidebar, pages) —
// they land on /login instead. /login, /setup and the break-glass setup
// path stay open so sign-in and first-run recovery remain reachable.
function RequireAuth({ children }: Readonly<{ children: ReactNode }>) {
  const { user, loading, error } = useWhoami();
  const { pathname } = useLocation();
  const open =
    pathname === '/login' ||
    pathname === '/setup' ||
    pathname.startsWith('/admin/system/setup');
  if (open) return <>{children}</>;
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="starfield-bg" aria-hidden="true" />
        <Loader2 className="h-6 w-6 animate-spin text-accent" aria-label="Loading" />
      </div>
    );
  }
  if (error || !user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <SetupRedirect>
    <RequireAuth>
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* v241 — first-run setup wizard (item 76 of PRODUCT-ROADMAP.md). */}
      <Route path="/setup" element={<SetupWizard />} />
      <Route path="/admin/system/setup" element={<AdminSystemSetup />} />

      <Route path="/admin" element={<AdminOverview />} />
      <Route path="/admin/maintenance" element={<AdminMaintenance />} />

      {/* v185 IA cleanup — Notifications hub. */}
      <Route path="/admin/notifications" element={<AdminNotificationsHub />} />
      <Route
        path="/admin/notification-channels"
        element={<Navigate replace to="/admin/notifications?tab=channels" />}
      />
      <Route
        path="/admin/webhooks"
        element={<Navigate replace to="/admin/notifications?tab=webhooks" />}
      />

      <Route path="/admin/audit" element={<AdminAudit />} />
      <Route path="/admin/permissions" element={<AdminPermissions />} />
      <Route path="/admin/quotas" element={<AdminQuotas />} />
      <Route path="/admin/scim" element={<AdminSCIM />} />

      {/* v185 IA cleanup — Imports hub. */}
      <Route path="/admin/imports" element={<AdminImportsHub />} />
      <Route
        path="/admin/imports/bulk"
        element={<Navigate replace to="/admin/imports?tab=bulk" />}
      />
      <Route path="/admin/license" element={<AdminLicense />} />
      <Route path="/admin/branding" element={<AdminBranding />} />
      <Route path="/admin/banners" element={<AdminBanners />} />
      {/* v220 air-gapped-mode-by-default settings (item 24 of PRODUCT-ROADMAP.md). */}
      <Route path="/admin/network" element={<AdminNetwork />} />
      {/* Consolidated trust surface — X.509 / Sigstore / OIDC tabs
          under one shell (item 91 Phase A of PRODUCT-ROADMAP.md). The
          three legacy URLs redirect onto the matching tab fragment so
          existing bookmarks keep working. */}
      <Route path="/admin/trust" element={<AdminTrust />} />
      <Route
        path="/admin/sigstore"
        element={<Navigate replace to="/admin/trust#sigstore" />}
      />
      <Route
        path="/admin/oidc/policies"
        element={<Navigate replace to="/admin/trust#oidc" />}
      />
      {/* Org-wide retention overview (item 40 of PRODUCT-ROADMAP.md). */}
      <Route path="/admin/retention" element={<AdminRetention />} />
      {/* CNPG ImageCatalog mirror (item 46 of PRODUCT-ROADMAP.md). */}
      <Route path="/admin/cnpg-catalog" element={<AdminCNPGCatalog />} />
      {/* Pull-Gate-Policy-Engine (item 47 of PRODUCT-ROADMAP.md, Phase B). */}
      <Route path="/admin/pull-gate" element={<AdminPullGate />} />
      {/* Cross-Instance Geo-Sync (item 48 of PRODUCT-ROADMAP.md, Phase A). */}
      <Route path="/admin/geo" element={<AdminGeo />} />
      {/* In-app API playground (item 55 Phase D / item 64 in delivery order). */}
      <Route path="/admin/api-reference" element={<AdminApiReference />} />
      <Route path="/admin/seed" element={<AdminSeed />} />
      {/* Slug-naming-convention audit (item 62 of PRODUCT-ROADMAP.md). */}
      <Route path="/admin/slug-audit" element={<AdminSlugAudit />} />
      {/* Consolidated compliance surface — ISO 27001 / SOC 2 tabs
          under one shell (item 91 Phase B of PRODUCT-ROADMAP.md). The
          two legacy URLs redirect onto the matching tab fragment so
          existing bookmarks keep working. */}
      <Route path="/admin/compliance" element={<AdminCompliance />} />
      <Route
        path="/admin/compliance/iso27001"
        element={<Navigate replace to="/admin/compliance#iso27001" />}
      />
      <Route
        path="/admin/compliance/soc2"
        element={<Navigate replace to="/admin/compliance#soc2" />}
      />
      {/* Platform-Monitoring observability snapshot (item 70 Phase F). */}
      <Route path="/admin/observability" element={<AdminObservability />} />
      {/* Admin → Support — UI variant of `orbital diag` bundle generator
          (item 100 Phase B of PRODUCT-ROADMAP.md). Backend endpoint
          /api/admin/support/generate-bundle landed in Phase A. */}
      <Route path="/admin/support" element={<AdminSupport />} />

      <Route path="/security" element={<SecurityList />} />
      <Route path="/security/new" element={<SecurityNew />} />
      <Route path="/security/audit" element={<SecurityAudit />} />
      <Route path="/security/who-pulled" element={<WhoPulled />} />
      <Route path="/security/advisories" element={<Advisories />} />
      <Route path="/security/advisories/:id" element={<AdvisoryDetail />} />

      {/* v161 Tier 0 Detection space — sibling to Security in the IA. */}
      <Route path="/detection/findings" element={<DetectionFindings />} />
      <Route path="/detection/findings/:id" element={<DetectionFindingDetail />} />
      <Route path="/detection/rules" element={<DetectionRules />} />
      <Route path="/detection/scanners" element={<DetectionScanners />} />
      <Route path="/detection/queue" element={<DetectionQueue />} />
      {/* v177 Tier 4 — aggregations dashboard. */}
      <Route path="/detection/dashboard" element={<DetectionDashboard />} />

      <Route
        path="/*"
        element={
          <AppShell>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/repos" element={<Repos />} />
              <Route path="/repos/:id" element={<RepoDetail />} />
              {/* Ollama-specific model browser (roadmap item 38). */}
              <Route path="/repos/:id/ollama" element={<OllamaModels />} />
              {/* Kustomize-Components OCI browser (roadmap item 43). */}
              <Route path="/repos/:id/kustomize" element={<KustomizeComponents />} />
              {/* OPA-Bundles browser (roadmap item 45). */}
              <Route path="/repos/:id/opa" element={<OPABundles />} />
              {/* Agent/Skill package browser (roadmap item 149 Phase B). */}
              <Route path="/repos/:id/agentskill" element={<AgentSkillPackages />} />
              {/* v193 — per-artifact detail page (item 17). Currently
                  surfaces the dependency-graph tab; future tabs land
                  on the same path. */}
              <Route path="/artifacts/:id" element={<ArtifactDetail />} />
              <Route path="/tokens" element={<Tokens />} />
              <Route path="/promotions" element={<Promotions />} />
              {/* v222 — Build-Info browse (item 33 Phase A). */}
              <Route path="/builds" element={<Builds />} />
              <Route path="/projects/:id/builds" element={<ProjectBuilds />} />
              {/* v224 — reverse dependency search (item 33 Phase C).
                  Mounted before the {name}/{number} route so React-Router
                  doesn't try to interpret "dependency-search" as a build
                  name. */}
              <Route
                path="/projects/:id/builds/dependency-search"
                element={<BuildDependencySearch />}
              />
              <Route
                path="/projects/:id/builds/:name/:number"
                element={<BuildDetail />}
              />
              {/* v186 — global artifact + repo search results page. */}
              <Route path="/search" element={<Search />} />
              <Route path="/service-accounts" element={<ServiceAccountsPage />} />
              <Route path="/projects/:id/members" element={<ProjectMembers />} />
              {/* v191 — per-project license-compliance dashboard. */}
              <Route
                path="/projects/:id/license-compliance"
                element={<LicenseCompliance />}
              />
              {/* v192 — per-project auto-quarantine settings (item 16). */}
              <Route
                path="/projects/:id/quarantine"
                element={<Quarantine />}
              />
              <Route path="/trash" element={<Trash />} />
              <Route path="/settings" element={<Settings />} />
            </Routes>
          </AppShell>
        }
      />
    </Routes>
    </RequireAuth>
    </SetupRedirect>
  );
}
